import React, { useContext, useMemo } from "react";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem, treeItemClasses } from "@mui/x-tree-view/TreeItem";
import { ThemeContext } from "../../../context/ThemeContext";
import { Box, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import FolderIcon from "@mui/icons-material/Folder";
import { styled, alpha } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { getAccount, getAccountSummary } from "../../../api/request/dashboard/dashboard";
import { setAccountData, setAccountId, setAccountSummary } from "../../../store/Slices/accountSummary";
import { setUserDetails } from "../../../store/Slices/userSlice";

const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.2, 0),
  },
  [`& .${treeItemClasses.iconContainer}`]: {
    "& .close": { opacity: 0.3 },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

function ExpandIcon(props) {
  return <AddBoxRoundedIcon {...props} sx={{ color: props.color, opacity: 0.8 }} />;
}

function CollapseIcon(props) {
  return <IndeterminateCheckBoxRoundedIcon {...props} sx={{ color: props.color, opacity: 0.8 }} />;
}

function EndIcon(props) {
  return <DisabledByDefaultRoundedIcon {...props} sx={{ color: props.color, opacity: 0.3 }} />;
}

function buildTree(data) {
  let map = new Map();

  data.forEach((item) => {
    let client = { ...item.client, accounts: item.accounts, children: [] };
    map.set(client.id, client);
  });


  let root = [];

  map.forEach((client) => {
    if (client.parentId && map.has(client.parentId)) {
      map.get(client.parentId).children.push(client);
    } else {
      root.push(client);
    }

  });


  return root;
}

export default function AccountTree({ setSelectedTab }) {
  const { darkMode } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const { accountId:accountID } = useSelector(state => state.accountSummary)
  const { accountTree = [] } = useSelector((state) => state.accountTree)
  const structuredTree = useMemo(() => buildTree(accountTree), [accountTree]);
  const iconColor = "#1E90FF";

  const handleAccountClick = async (account,node) => {
    try {
      const response = await getAccount(account.id);
      dispatch(setAccountData(response?.data || {}));
      dispatch(setAccountId(account.id))
      dispatch(setUserDetails({id:account.clientId,username:node.userName}))
      fetchAccountSummary(account.id);
      document.title = `${account.id} - ${node.userName} - Vertex Trader`
      setSelectedTab(0)
    } catch (error) {
      console.error("❌ Error fetching account data:", error);
    }
  };

  const fetchAccountSummary = async (accountID) => {
    try {
      const response = await getAccountSummary(accountID);
      dispatch(setAccountSummary(response?.data || {}));
    } catch (error) {
      console.error("Error fetching account summary:", error);
      dispatch(setAccountSummary({}));
    }
  };

  const renderTree = (data) => {
    if (!data || !Array.isArray(data)) return null;

    return data.map((node) => {
      return (
        <CustomTreeItem key={node.id} itemId={String(node.id)}
          label={
            <Box display="flex" alignItems="center">
              <FolderIcon sx={{ fontSize: 18, mr: 1, color: iconColor }} />
              <Typography sx={{ color: darkMode ? "var(--lightText)" : "var(--darkText)", fontSize: 14, }}>
                {node.fullName || node.userName}
              </Typography>
            </Box>
          }
        >
          {node.accounts &&
            Object.values(node.accounts).map((account) => {
              const isActive = account.id === accountID;
              return (
                <CustomTreeItem onClick={() => handleAccountClick(account,node)} key={account.id} itemId={String(account.id)} label={
                  <Typography className="py-1 px-3" variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--darkText)", backgroundColor: isActive ? "rgba(30, 144, 255, 0.2)" : "transparent", }}>
                    {node.fullName} : {account.id}
                  </Typography>
                }
                  endIcon={<PersonIcon sx={{ fontSize: 18, color: iconColor }} />}
                />
              );
            })}

          {node.children && renderTree(node.children)}
        </CustomTreeItem>
      );
    });
  };



  return (
    <SimpleTreeView aria-label="customized" sx={{ overflowX: "hidden", minHeight: 270, flexGrow: 1, maxWidth: 600 }}
      slots={{
        expandIcon: (props) => <ExpandIcon {...props} color={iconColor} />,
        collapseIcon: (props) => <CollapseIcon {...props} color={iconColor} />,
        endIcon: (props) => <EndIcon {...props} color={iconColor} />,
      }}
    >
      {structuredTree.length > 0 ? renderTree(structuredTree) : <p>Loading accounts...</p>}
    </SimpleTreeView>
  );
}
